import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-policy-page">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl sm:text-5xl font-heading font-black tracking-tight mb-4">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 29, 2025</p>
          
          <div className="prose prose-invert max-w-none space-y-8">
            <section>
              <p className="text-lg leading-relaxed">
                At Xtream Store, your privacy is important to us. This Privacy Policy explains how we collect, use, store, and protect your information when you visit or make a purchase from our website.
              </p>
              <p className="text-muted-foreground">
                By using Xtream Store, you agree to the terms of this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">1. Information We Collect</h2>
              <p>We may collect the following types of information:</p>
              
              <h3 className="text-xl font-heading font-bold mt-4 mb-2">a) Personal Information</h3>
              <p>When you place an order or contact us, we may collect:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Name or username</li>
                <li>Email address</li>
                <li>Contact details (such as Telegram or WhatsApp, if provided)</li>
                <li>Payment-related information (transaction ID, payment method — not card details)</li>
              </ul>
              
              <h3 className="text-xl font-heading font-bold mt-4 mb-2">b) Non-Personal Information</h3>
              <p>Automatically collected information may include:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>IP address</li>
                <li>Browser type</li>
                <li>Device information</li>
                <li>Pages visited and time spent on the website</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">This data helps us improve user experience and website performance.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">2. How We Use Your Information</h2>
              <p>We use collected information to:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Process and deliver your digital purchases</li>
                <li>Communicate order updates and support responses</li>
                <li>Prevent fraud, misuse, or unauthorized activities</li>
                <li>Improve our services and website functionality</li>
              </ul>
              <p className="font-bold mt-4">We do not sell or rent your personal information to third parties.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">3. Payments & Transactions</h2>
              <p>All payments on Xtream Store are handled through secure third-party payment gateways.</p>
              <p className="font-bold mt-2">We do not store:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Debit card details</li>
                <li>Credit card details</li>
                <li>Net banking credentials</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">Only transaction references required for order verification are retained.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">4. Digital Products Disclaimer</h2>
              <p>Xtream Store operates as a digital marketplace providing access to various digital services and subscriptions at discounted prices.</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
                <li>Product availability and access duration may vary</li>
                <li>We are not officially affiliated with any OTT platform unless explicitly stated</li>
                <li>Users are responsible for complying with local laws and platform terms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">5. Cookies & Tracking Technologies</h2>
              <p>Xtream Store may use cookies to:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Remember user preferences</li>
                <li>Analyze traffic and performance</li>
                <li>Improve site experience</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">You can disable cookies in your browser settings, but some features may not function properly.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">6. Data Security</h2>
              <p>We take reasonable steps to protect your data using:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Secure servers</li>
                <li>Restricted access controls</li>
                <li>Regular monitoring</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">However, no online platform can guarantee 100% security, and users share information at their own risk.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">7. Third-Party Services</h2>
              <p>We may use third-party tools for:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Payment processing</li>
                <li>Website analytics</li>
                <li>Customer communication</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">These services have their own privacy policies, and Xtream Store is not responsible for their practices.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">8. Refunds & Disputes</h2>
              <p>Privacy-related data used during disputes or refunds will be handled confidentially and only for resolution purposes.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">9. Children's Privacy</h2>
              <p>Xtream Store is not intended for users under the age of 18.</p>
              <p className="text-muted-foreground">We do not knowingly collect personal data from minors.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">10. Policy Updates</h2>
              <p>We may update this Privacy Policy from time to time.</p>
              <p className="text-muted-foreground">Any changes will be posted on this page with an updated date.</p>
              <p className="text-sm text-muted-foreground mt-2">Continued use of the website means acceptance of the revised policy.</p>
            </section>

            <section className="bg-white/5 p-6 rounded-xl border border-white/10">
              <h2 className="text-2xl font-heading font-bold mb-4">11. Contact Us</h2>
              <p>If you have any questions or concerns regarding this Privacy Policy, you can contact us at:</p>
              <div className="mt-4 space-y-2">
                <p className="flex items-center gap-2">
                  <span className="font-bold">Email:</span>
                  <a href="mailto:Support@xtreamstore.in" className="text-primary hover:underline">
                    Support@xtreamstore.in
                  </a>
                </p>
                <p><span className="font-bold">Platform:</span> Xtream Store</p>
              </div>
            </section>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;